import { useQuery } from '@tanstack/react-query';
import { differenceInDays, endOfDay, format, startOfDay, subDays } from 'date-fns';
import { Download, Eye, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { DateTimeDisplay } from '../components/common/DateTimeDisplay';
import { ExportTrackerModal } from '../components/ExportTrackerModal';
import { Layout } from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DatePicker } from '../components/ui/date-picker';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { usePortalConfig } from '../hooks/usePortalConfig';
import { cn } from '../lib/utils';
import { ResolvedColumn } from '../registry/resolvePortalConfig';
import { getTransactions } from '../services/reportService';
import { useAuthStore } from '../store/authStore';
import { ITransactionRow } from '../types';

const fmtAmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n ?? 0);

const STATUS_COLORS: Record<string, string> = {
  won: '#10CFA3',
  lost: '#f87171',
  void: '#fbbf24',
};
const STATUS_LABELS: Record<string, string> = {
  won: 'Won',
  lost: 'Lost',
  void: 'Cancelled',
};

const ROUND_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  debit_completed: { bg: 'rgba(59,130,246,0.12)', text: '#60a5fa' },
  credit_started: { bg: 'rgba(6,182,212,0.12)', text: '#22d3ee' },
  credit_completed: { bg: 'rgba(16,207,163,0.12)', text: '#10CFA3' },
  refund_completed: { bg: 'rgba(251,191,36,0.12)', text: '#fbbf24' },
  debit_failed: { bg: 'rgba(239,68,68,0.12)', text: '#f87171' },
  credit_failed: { bg: 'rgba(249,115,22,0.12)', text: '#fb923c' },
  refund_failed: { bg: 'rgba(244,63,94,0.12)', text: '#fb7185' },
  refund_started: { bg: 'rgba(168,85,247,0.12)', text: '#c084fc' },
};

type TxColType = 'text' | 'mono' | 'amount' | 'date' | 'smallText' | 'badge' | 'roundStatus';

const TX_COL_TYPE: Record<string, TxColType> = {
  roundId: 'mono',
  betTxnId: 'mono',
  betPlacedAt: 'date',
  betSettledAt: 'date',
  operator: 'text',
  betAmount: 'amount',
  winAmount: 'amount',
  playerCurrencyCode: 'smallText',
  resolved: 'badge',
  status: 'roundStatus',
  marketType: 'text',
  transactionType: 'text',
  externalPlayerId: 'text',
  playerName: 'text',
};

type TxCellRenderer = (value: ITransactionRow[keyof ITransactionRow], colId: string) => React.ReactNode;

const TX_CELL_RENDERERS: Record<TxColType, TxCellRenderer> = {
  badge: (value, colId) => (
    <TableCell key={colId}>
      <span className="text-xs font-semibold" style={{ color: STATUS_COLORS[value as string] || 'inherit' }}>
        {STATUS_LABELS[value as string] || (value as string) || '-'}
      </span>
    </TableCell>
  ),
  roundStatus: (value, colId) => {
    const s = value as string;
    const colors = ROUND_STATUS_COLORS[s];
    return (
      <TableCell key={colId}>
        {s ? (
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap"
            style={colors ? { background: colors.bg, color: colors.text } : { color: 'inherit' }}
          >
            {s}
          </span>
        ) : (
          '-'
        )}
      </TableCell>
    );
  },
  date: (value, colId) => (
    <TableCell key={colId} className="text-sm whitespace-nowrap">
      <DateTimeDisplay date={value as string} />
    </TableCell>
  ),
  amount: (value, colId) => (
    <TableCell key={colId} className="text-right tabular-nums text-sm">
      {fmtAmt(value as number)}
    </TableCell>
  ),
  mono: (value, colId) => (
    <TableCell key={colId} className="font-mono text-xs text-muted-foreground whitespace-nowrap">
      {(value as string) ?? '-'}
    </TableCell>
  ),
  smallText: (value, colId) => (
    <TableCell key={colId} className="text-xs text-muted-foreground">
      {value as string}
    </TableCell>
  ),
  text: (value, colId) => (
    <TableCell key={colId} className="text-sm">
      {(value as string) ?? '-'}
    </TableCell>
  ),
};

function getTxCellValue(tx: ITransactionRow, col: ResolvedColumn): React.ReactNode {
  const value = tx[col.id as keyof ITransactionRow];
  const type = TX_COL_TYPE[col.id] ?? 'text';
  return TX_CELL_RENDERERS[type](value, col.id);
}

export const Transactions = () => {
  const today = new Date();
  const defaultStart = subDays(today, 1);
  const defaultEnd = today;

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [startDate, setStartDate] = useState<Date | undefined>(() => {
    const param = searchParams.get('startDate');
    return param ? new Date(param) : defaultStart;
  });
  const [endDate, setEndDate] = useState<Date | undefined>(() => {
    const param = searchParams.get('endDate');
    return param ? new Date(param) : defaultEnd;
  });
  const [status, setStatus] = useState(() => searchParams.get('status') || '');
  const [roundId, setRoundId] = useState(() => searchParams.get('roundId') || '');
  const [betTxnId, setBetTxnId] = useState(() => searchParams.get('betTxnId') || '');
  const [platformTransactionId, setPlatformTransactionId] = useState(
    () => searchParams.get('platformTransactionId') || '',
  );
  const [externalPlayerId, setExternalPlayerId] = useState(() => searchParams.get('externalPlayerId') || '');
  const [transactionType, setTransactionType] = useState(() => searchParams.get('transactionType') || '');
  const [roundStatus, setRoundStatus] = useState(() => searchParams.get('roundStatus') || '');
  const [selectedOperatorId, setSelectedOperatorId] = useState(() => searchParams.get('operatorId') || '');

  const pageNo = Number(searchParams.get('pageNo')) || 1;
  const [rangeError, setRangeError] = useState('');
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const hasAppliedFilters = searchParams.has('startDate');
  const appliedFilters: Record<string, unknown> = {
    startDate: searchParams.get('startDate') || startOfDay(defaultStart).toISOString(),
    endDate: searchParams.get('endDate') || endOfDay(defaultEnd).toISOString(),
    ...(searchParams.get('status') && { status: searchParams.get('status') }),
    ...(searchParams.get('roundId') && { roundId: searchParams.get('roundId') }),
    ...(searchParams.get('betTxnId') && { betTxnId: searchParams.get('betTxnId') }),
    ...(searchParams.get('platformTransactionId') && {
      platformTransactionId: searchParams.get('platformTransactionId'),
    }),
    ...(searchParams.get('externalPlayerId') && { externalPlayerId: searchParams.get('externalPlayerId') }),
    ...(searchParams.get('transactionType') && { transactionType: searchParams.get('transactionType') }),
    ...(searchParams.get('roundStatus') && { roundStatus: searchParams.get('roundStatus') }),
    ...(searchParams.get('operatorId') && { operatorId: searchParams.get('operatorId') }),
    pageNo,
  };

  const { user } = useAuthStore();
  const operatorIds = user?.operatorIds ?? [];

  const portalConfig = usePortalConfig();
  const pageConfig = portalConfig.pages.find((p) => p.id === 'transactions');
  const { maxDateRangeDays: MAX_DAYS } = portalConfig;

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['transactions', appliedFilters],
    queryFn: () => getTransactions(appliedFilters),
  });

  if (pageConfig && !pageConfig.enabled) {
    return <Navigate to="/" replace />;
  }

  const tableConfig = pageConfig?.tables?.[0];
  const visibleCols = (tableConfig?.columns ?? []).filter((c) => c.visible);

  const applyFilters = () => {
    if (!startDate || !endDate) {
      setRangeError('Please select both start and end dates.');
      return;
    }
    const diffDays = differenceInDays(endDate, startDate);
    if (diffDays < 0) {
      setRangeError('Start date must be before end date.');
      return;
    }
    if (diffDays >= MAX_DAYS) {
      setRangeError(`Date range cannot exceed ${MAX_DAYS} days.`);
      return;
    }
    setRangeError('');
    const effectiveStatus = status && status !== 'all' ? status : '';
    const effectiveTransactionType = transactionType && transactionType !== 'all' ? transactionType : '';
    const effectiveRoundStatus = roundStatus && roundStatus !== 'all' ? roundStatus : '';
    const effectiveOperatorId = selectedOperatorId && selectedOperatorId !== 'all' ? selectedOperatorId : '';
    const params: Record<string, string> = {
      startDate: startOfDay(startDate).toISOString(),
      endDate: endOfDay(endDate).toISOString(),
      pageNo: '1',
    };
    if (effectiveStatus) params.status = effectiveStatus;
    if (roundId.trim()) params.roundId = roundId.trim();
    if (betTxnId.trim()) params.betTxnId = betTxnId.trim();
    if (platformTransactionId.trim()) params.platformTransactionId = platformTransactionId.trim();
    if (externalPlayerId.trim()) params.externalPlayerId = externalPlayerId.trim();
    if (effectiveTransactionType) params.transactionType = effectiveTransactionType;
    if (effectiveRoundStatus) params.roundStatus = effectiveRoundStatus;
    if (effectiveOperatorId) params.operatorId = effectiveOperatorId;
    setSearchParams(params);
  };

  const clearFilters = () => {
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setStatus('');
    setRoundId('');
    setBetTxnId('');
    setPlatformTransactionId('');
    setExternalPlayerId('');
    setTransactionType('');
    setRoundStatus('');
    setSelectedOperatorId('');
    setRangeError('');
    setSearchParams({});
  };

  const handlePrevPage = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('pageNo', String(pageNo - 1));
      return params;
    });
  };

  const handleNextPage = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('pageNo', String(pageNo + 1));
      return params;
    });
  };

  const hasNextPage = (data?.data?.length ?? 0) >= 25;

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Transactions</h1>
            <p className="text-muted-foreground text-sm mt-1">Detailed bet-level transaction records</p>
          </div>
          <Button
            variant="ghost"
            onClick={() => setExportModalOpen(true)}
            className="gap-2 flex-shrink-0 w-full sm:w-auto border border-[#10CFA3] text-[#10CFA3] hover:bg-[#10CFA3] hover:text-white"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
        </div>

        <Card>
          <CardContent className="pt-5 pb-5">
            <div className="flex items-end gap-4 flex-wrap">
              <div className="space-y-2">
                <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">From</Label>
                <DatePicker
                  value={startDate}
                  onChange={(d) => {
                    setStartDate(d);
                    setRangeError('');
                  }}
                  placeholder="Start date"
                  toDate={endDate ?? today}
                />
              </div>
              <div className="space-y-2">
                <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">To</Label>
                <DatePicker
                  value={endDate}
                  onChange={(d) => {
                    setEndDate(d);
                    setRangeError('');
                  }}
                  placeholder="End date"
                  fromDate={startDate}
                  toDate={today}
                />
              </div>
              <div className="space-y-2">
                <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Status
                </Label>
                <Select value={status || 'all'} onValueChange={(val) => setStatus(val === 'all' ? '' : val)}>
                  <SelectTrigger className="w-36 h-9">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                    <SelectItem value="void">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Txn Type
                </Label>
                <Select
                  value={transactionType || 'all'}
                  onValueChange={(val) => setTransactionType(val === 'all' ? '' : val)}
                >
                  <SelectTrigger className="w-40 h-9">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="BET_PLACED">BET_PLACED</SelectItem>
                    <SelectItem value="COIN_PURCHASED">COIN_PURCHASED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Round Status
                </Label>
                <Select value={roundStatus || 'all'} onValueChange={(val) => setRoundStatus(val === 'all' ? '' : val)}>
                  <SelectTrigger className="w-44 h-9">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="debit_completed">debit_completed</SelectItem>
                    <SelectItem value="credit_completed">credit_completed</SelectItem>
                    <SelectItem value="refund_completed">refund_completed</SelectItem>
                    <SelectItem value="debit_failed">debit_failed</SelectItem>
                    <SelectItem value="credit_failed">credit_failed</SelectItem>
                    <SelectItem value="refund_failed">refund_failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {operatorIds.length >= 2 && (
                <div className="space-y-2">
                  <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Operator
                  </Label>
                  <Select
                    value={selectedOperatorId || 'all'}
                    onValueChange={(val) => setSelectedOperatorId(val === 'all' ? '' : val)}
                  >
                    <SelectTrigger className="w-44 h-9">
                      <SelectValue placeholder="All operators" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {operatorIds.map((op) => (
                        <SelectItem key={op._id} value={op._id}>
                          {op.operatorName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex items-end gap-4 flex-wrap mt-4">
              <div className="space-y-2">
                <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Round ID
                </Label>
                <Input
                  className="w-48 h-9 text-sm"
                  placeholder="Round ID"
                  value={roundId}
                  onChange={(e) => setRoundId(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Bet Txn ID
                </Label>
                <Input
                  className="w-48 h-9 text-sm"
                  placeholder="Bet Txn ID"
                  value={betTxnId}
                  onChange={(e) => setBetTxnId(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Platform Txn ID
                </Label>
                <Input
                  className="w-48 h-9 text-sm"
                  placeholder="Platform Txn ID"
                  value={platformTransactionId}
                  onChange={(e) => setPlatformTransactionId(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  External Player ID
                </Label>
                <Input
                  className="w-48 h-9 text-sm"
                  placeholder="External Player ID"
                  value={externalPlayerId}
                  onChange={(e) => setExternalPlayerId(e.target.value)}
                />
              </div>
              <Button
                onClick={applyFilters}
                className="text-white font-medium"
                style={{ background: '#8C16C7', border: 'none' }}
              >
                Apply filters
              </Button>
              {hasAppliedFilters && (
                <Button variant="ghost" onClick={clearFilters} className="text-muted-foreground gap-1.5">
                  <X className="w-3.5 h-3.5" />
                  Clear
                </Button>
              )}
            </div>

            {rangeError && (
              <div
                className="mt-3 text-sm p-3 rounded-lg"
                style={{
                  background: 'rgba(239,68,68,0.08)',
                  color: '#fca5a5',
                  border: '1px solid rgba(239,68,68,0.20)',
                }}
              >
                {rangeError}
              </div>
            )}
            {hasAppliedFilters && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">Applied:</span>
                <Badge variant="secondary" className="text-xs font-normal">
                  {format(new Date(appliedFilters.startDate as string), 'dd MMM yyyy')} -{' '}
                  {format(new Date(appliedFilters.endDate as string), 'dd MMM yyyy')}
                </Badge>
                {!!appliedFilters.status && (
                  <Badge variant="secondary" className="text-xs font-normal">
                    Status: {STATUS_LABELS[appliedFilters.status as string] || String(appliedFilters.status)}
                  </Badge>
                )}
                {!!appliedFilters.transactionType && (
                  <Badge variant="secondary" className="text-xs font-normal">
                    Txn Type: {String(appliedFilters.transactionType)}
                  </Badge>
                )}
                {!!appliedFilters.roundStatus && (
                  <Badge variant="secondary" className="text-xs font-normal">
                    Round Status: {String(appliedFilters.roundStatus)}
                  </Badge>
                )}
                {!!appliedFilters.roundId && (
                  <Badge variant="secondary" className="text-xs font-normal">
                    Round ID: {String(appliedFilters.roundId)}
                  </Badge>
                )}
                {!!appliedFilters.betTxnId && (
                  <Badge variant="secondary" className="text-xs font-normal">
                    Bet Txn ID: {String(appliedFilters.betTxnId)}
                  </Badge>
                )}
                {!!appliedFilters.externalPlayerId && (
                  <Badge variant="secondary" className="text-xs font-normal">
                    External Player: {String(appliedFilters.externalPlayerId)}
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-base">{tableConfig?.title ?? 'Results'}</CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="gap-1.5 h-8 text-xs"
                >
                  <RefreshCw className={cn('w-3 h-3', isFetching && 'animate-spin')} />
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNo === 1}
                  onClick={handlePrevPage}
                  className="h-8 text-xs"
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-1">Page {pageNo}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasNextPage}
                  onClick={handleNextPage}
                  className="h-8 text-xs"
                >
                  Next
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-11 w-full" />
                ))}
              </div>
            ) : isError ? (
              <div
                className="text-sm p-4 rounded-lg"
                style={{
                  background: 'rgba(239,68,68,0.08)',
                  color: '#fca5a5',
                  border: '1px solid rgba(239,68,68,0.20)',
                }}
              >
                Failed to load transactions. Please try again.
              </div>
            ) : !data?.data?.length ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                No transactions found for the selected criteria.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-9" />
                      {visibleCols.map((col) => (
                        <TableHead key={col.id} className={col.align === 'right' ? 'text-right' : undefined}>
                          {col.label}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.data.map((tx) => (
                      <TableRow key={tx._id}>
                        <TableCell className="w-9 pr-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:bg-[#10CFA3]/15 hover:text-foreground"
                            onClick={() =>
                              navigate(`/transactions/${tx.betTxnId}`, {
                                state: { from: location.pathname + location.search },
                              })
                            }
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                        {visibleCols.map((col) => getTxCellValue(tx, col))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ExportTrackerModal
        open={exportModalOpen}
        onOpenChange={setExportModalOpen}
        exportType="transactions"
        currentFilters={appliedFilters}
      />
    </Layout>
  );
};
